import { createContext, useContext, useState, useEffect } from "react";

/**
 * WebView / native-app detection.
 *
 * IMPORTANT design choices (Feb 2026 refactor — fix false positives):
 *
 *   1. The ONLY reliable signals for "this is the installed Android/iOS app
 *      wrapper" are explicit ones the wrapper controls:
 *        - A JS bridge object (`window.FuturaApp`, `window.AndroidInterface`,
 *          `window.webkit.messageHandlers.futura`)
 *        - A URL query param (`?source=app` / `?webview=true`) injected by
 *          the native code on initial load.
 *        - The Android WebView UA contains `; wv)` or `; wv;` — this is
 *          standardized by Google and present in Android Studio WebViews.
 *   2. We DO NOT use heuristics that catch legitimate mobile browsers:
 *        - "Android + Version/" → also matches Samsung Internet (web!)
 *        - "iPhone without Safari/" → also matches Chrome iOS, Firefox iOS,
 *          Edge iOS, DuckDuckGo iOS (web!)
 *      Those rules caused users on these browsers to lose the pricing page,
 *      which violates the user goal.
 *   3. Facebook / Instagram / LinkedIn in-app browsers ARE treated as app
 *      because they're effectively WebViews and we shouldn't show paywall
 *      flows from there either (Google Play / Apple policies).
 *   4. Display-mode standalone (PWA install) → app mode.
 *   5. Persistence: we ONLY persist to localStorage when detection came from
 *      a STRONG signal (bridge or URL param). UA / standalone hits are NOT
 *      persisted to avoid trapping the user in app-mode forever if they
 *      later open the web from a normal browser on the same device storage.
 */

const WebViewContext = createContext(false);
const STORAGE_KEY = "futura_app_mode_v2";
const LEGACY_STORAGE_KEY = "futura_app_mode";

function safeLSGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeLSSet(key, val) {
  try { localStorage.setItem(key, val); } catch { /* localStorage unavailable */ }
}
function safeLSRemove(key) {
  try { localStorage.removeItem(key); } catch { /* localStorage unavailable */ }
}

// One-time cleanup: previous detector could persist false positives (Samsung
// Internet, Chrome iOS, Firefox iOS). Wipe the legacy key so those users get a
// fresh evaluation on next load.
safeLSRemove(LEGACY_STORAGE_KEY);

function persistAppMode() {
  safeLSSet(STORAGE_KEY, "1");
}

/** Strong signals — confidently the installed-app wrapper. Persist. */
function hasStrongAppSignal() {
  // 1. JS bridge injected by the native code
  if (
    typeof window !== "undefined" &&
    (window.FuturaApp ||
      window.AndroidInterface ||
      (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.futura))
  ) {
    return true;
  }
  // 2. Explicit URL hint added by the native wrapper on first load
  const href = (typeof window !== "undefined" && window.location && window.location.href) || "";
  const hash = (typeof window !== "undefined" && window.location && window.location.hash) || "";
  if (
    href.includes("source=app") ||
    href.includes("webview=true") ||
    hash.includes("source=app")
  ) {
    return true;
  }
  return false;
}

/** Weak signals — heuristic, do NOT persist (a browser update could flip them). */
function hasWeakAppSignal() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";

  // Android WebView (Google-standardized marker `wv`).
  // Examples that MATCH:   `Mozilla/5.0 (Linux; Android 13; ...; wv) AppleWebKit/...`
  // Examples that DON'T:   any Chrome / Samsung Internet / Firefox mobile UA
  if (/;\s*wv[);]/.test(ua)) return true;

  // Facebook / Instagram / LinkedIn / TikTok in-app browsers — also subject
  // to Google Play / Apple policies and shouldn't display paywalls.
  if (/FBAN|FBAV|Instagram|LinkedInApp|TikTokWebview|TwitterAndroid/i.test(ua)) return true;

  // Installed as a PWA (display-mode standalone) → treat as app.
  try {
    if (
      typeof window !== "undefined" &&
      ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        window.navigator.standalone === true)
    ) {
      return true;
    }
  } catch { /* matchMedia unavailable */ }

  return false;
}

function checkIsAppMode() {
  // Strong signal → persist + return true.
  if (hasStrongAppSignal()) {
    persistAppMode();
    return true;
  }
  // If previously persisted from a strong signal, keep app mode.
  if (safeLSGet(STORAGE_KEY) === "1") return true;
  // Weak signal → app mode but DON'T persist.
  if (hasWeakAppSignal()) return true;
  return false;
}

export function clearAppMode() {
  safeLSRemove(STORAGE_KEY);
  safeLSRemove(LEGACY_STORAGE_KEY);
}

export const detectWebView = checkIsAppMode;

export const WebViewProvider = ({ children }) => {
  const [isApp, setIsApp] = useState(checkIsAppMode);

  useEffect(() => {
    const check = () => {
      const next = checkIsAppMode();
      setIsApp((prev) => (prev !== next ? next : prev));
    };
    check();
    window.addEventListener("popstate", check);
    window.addEventListener("hashchange", check);
    return () => {
      window.removeEventListener("popstate", check);
      window.removeEventListener("hashchange", check);
    };
  }, []);

  return <WebViewContext.Provider value={isApp}>{children}</WebViewContext.Provider>;
};

export const useIsWebView = () => useContext(WebViewContext);
